import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BarChart3, Info, Users } from "lucide-react";
import { Suspense } from "react";
import { useProjectContext } from "~/components/layout/project-context";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Progress } from "~/components/ui/progress";
import {
  agreementStatsQueryOptions,
  cohensKappaQueryOptions,
  reviewerStatsQueryOptions,
  screeningSettingsQueryOptions,
} from "~/lib/screening/queries";

export const Route = createFileRoute("/_auth/projects/$projectId/statistics/")({
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(
        screeningSettingsQueryOptions(params.projectId),
      ),
      context.queryClient.ensureQueryData(agreementStatsQueryOptions(params.projectId)),
      context.queryClient.ensureQueryData(cohensKappaQueryOptions(params.projectId)),
      context.queryClient.ensureQueryData(reviewerStatsQueryOptions(params.projectId)),
    ]);
  },
  component: StatisticsPage,
});

function StatisticsPage() {
  const { projectId, projectTitle } = useProjectContext();
  return (
    <div className="flex min-h-screen flex-col">
      <main className="flex-1 p-6">
        <div className="mb-6">
          <h2 className="text-lg font-semibold">Screening Statistics</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Inter-rater reliability and reviewer performance metrics.
          </p>
        </div>

        <Suspense
          fallback={
            <div className="text-muted-foreground py-8 text-center">
              Loading statistics...
            </div>
          }
        >
          <StatisticsContent projectId={projectId} />
        </Suspense>
      </main>
    </div>
  );
}

function StatisticsContent({ projectId }: { projectId: string }) {
  const { data: settings } = useSuspenseQuery(screeningSettingsQueryOptions(projectId));
  const { data: agreementStats } = useSuspenseQuery(
    agreementStatsQueryOptions(projectId),
  );
  const { data: kappa } = useSuspenseQuery(cohensKappaQueryOptions(projectId));
  const { data: reviewerStats } = useSuspenseQuery(reviewerStatsQueryOptions(projectId));

  const isDualMode = settings.screeningMode === "dual";

  if (!isDualMode) {
    return (
      <div className="rounded-lg border p-8 text-center">
        <BarChart3 className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
        <h2 className="mb-2 text-lg font-semibold">Statistics Not Available</h2>
        <p className="text-muted-foreground mb-4">
          Inter-rater statistics require dual-screening mode to be enabled.
        </p>
        <Button
          variant="outline"
          render={
            <Link
              to="/projects/$projectId/settings"
              params={{ projectId }}
              search={{ tab: "dual-screening" }}
            />
          }
        >
          Enable Dual Screening
        </Button>
      </div>
    );
  }

  const completedPairs =
    agreementStats.agreed + agreementStats.conflict + agreementStats.adjudicated;
  const agreementPercent = Math.round(agreementStats.agreementRate * 100);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="p-4">
          <div className="text-muted-foreground mb-1 text-sm">
            Citation Pairs Reviewed
          </div>
          <div className="text-2xl font-bold">{completedPairs}</div>
          <div className="text-muted-foreground mt-1 text-xs">
            {agreementStats.pending + agreementStats.awaitingSecond} pending
          </div>
        </Card>

        <Card className="p-4">
          <div className="text-muted-foreground mb-1 text-sm">Agreement Rate</div>
          <div className="text-2xl font-bold">{agreementPercent}%</div>
          <Progress value={agreementPercent} className="mt-2 h-2" />
        </Card>

        <Card className="p-4">
          <div className="text-muted-foreground mb-1 text-sm">Conflicts Pending</div>
          <div className="text-2xl font-bold">{agreementStats.conflict}</div>
          <div className="text-muted-foreground mt-1 text-xs">
            {agreementStats.adjudicated} resolved
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold">Cohen's Kappa</h3>
            <p className="text-muted-foreground text-sm">
              Inter-rater reliability coefficient
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">
              {kappa.totalPairs > 0 ? kappa.kappa.toFixed(3) : "N/A"}
            </div>
            <div className="text-muted-foreground text-sm">{kappa.interpretation}</div>
          </div>
        </div>

        {kappa.totalPairs > 0 && (
          <>
            <div className="mb-4 grid gap-4 md:grid-cols-2">
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="text-muted-foreground text-sm">Observed Agreement</div>
                <div className="text-lg font-medium">
                  {(kappa.observedAgreement * 100).toFixed(1)}%
                </div>
              </div>
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="text-muted-foreground text-sm">Expected Agreement</div>
                <div className="text-lg font-medium">
                  {(kappa.expectedAgreement * 100).toFixed(1)}%
                </div>
              </div>
            </div>

            <div className="mt-4">
              <h4 className="mb-2 text-sm font-medium">Confusion Matrix</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className="p-2 text-left"></th>
                      <th className="text-muted-foreground p-2 text-center">
                        R2: Include
                      </th>
                      <th className="text-muted-foreground p-2 text-center">
                        R2: Exclude
                      </th>
                      <th className="text-muted-foreground p-2 text-center">R2: Maybe</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="text-muted-foreground p-2">R1: Include</td>
                      <td className="bg-green-100 p-2 text-center font-medium dark:bg-green-900/30">
                        {kappa.matrix[0][0]}
                      </td>
                      <td className="p-2 text-center">{kappa.matrix[0][1]}</td>
                      <td className="p-2 text-center">{kappa.matrix[0][2]}</td>
                    </tr>
                    <tr>
                      <td className="text-muted-foreground p-2">R1: Exclude</td>
                      <td className="p-2 text-center">{kappa.matrix[1][0]}</td>
                      <td className="bg-green-100 p-2 text-center font-medium dark:bg-green-900/30">
                        {kappa.matrix[1][1]}
                      </td>
                      <td className="p-2 text-center">{kappa.matrix[1][2]}</td>
                    </tr>
                    <tr>
                      <td className="text-muted-foreground p-2">R1: Maybe</td>
                      <td className="p-2 text-center">{kappa.matrix[2][0]}</td>
                      <td className="p-2 text-center">{kappa.matrix[2][1]}</td>
                      <td className="bg-green-100 p-2 text-center font-medium dark:bg-green-900/30">
                        {kappa.matrix[2][2]}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-muted-foreground mt-2 text-xs">
                R1 = First reviewer, R2 = Second reviewer. Green cells show agreement.
              </p>
            </div>
          </>
        )}

        {kappa.totalPairs === 0 && (
          <div className="text-muted-foreground rounded-lg border border-dashed p-4 text-center">
            <p>Not enough completed citation pairs to calculate Kappa.</p>
            <p className="mt-1 text-sm">
              At least 2 reviewers need to screen the same citations.
            </p>
          </div>
        )}

        <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-900 dark:bg-blue-950/30">
          <div className="flex items-start gap-2">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
            <div className="text-xs text-blue-800 dark:text-blue-200">
              <p className="mb-1 font-medium">Interpreting Cohen's Kappa</p>
              <ul className="space-y-0.5">
                <li>&lt; 0.20: Slight agreement</li>
                <li>0.21 - 0.40: Fair agreement</li>
                <li>0.41 - 0.60: Moderate agreement</li>
                <li>0.61 - 0.80: Substantial agreement</li>
                <li>&gt; 0.80: Almost perfect agreement</li>
              </ul>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="mb-4 flex items-center gap-2">
          <Users className="text-muted-foreground h-5 w-5" />
          <h3 className="text-lg font-semibold">Reviewer Breakdown</h3>
        </div>

        {reviewerStats.length === 0 ? (
          <div className="text-muted-foreground py-4 text-center">
            No reviewer statistics available yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="p-2 text-left font-medium">Reviewer</th>
                  <th className="p-2 text-center font-medium">Total</th>
                  <th className="p-2 text-center font-medium text-green-600 dark:text-green-400">
                    Include
                  </th>
                  <th className="p-2 text-center font-medium text-red-600 dark:text-red-400">
                    Exclude
                  </th>
                  <th className="p-2 text-center font-medium text-yellow-600 dark:text-yellow-400">
                    Maybe
                  </th>
                  <th className="p-2 text-center font-medium">Avg Time</th>
                </tr>
              </thead>
              <tbody>
                {reviewerStats.map((reviewer) => (
                  <tr key={reviewer.reviewerId} className="border-b last:border-b-0">
                    <td className="p-2 font-medium">{reviewer.reviewerName}</td>
                    <td className="p-2 text-center">{reviewer.totalScreened}</td>
                    <td className="p-2 text-center text-green-600 dark:text-green-400">
                      {reviewer.includeCount}
                      <span className="text-muted-foreground ml-1 text-xs">
                        (
                        {reviewer.totalScreened > 0
                          ? Math.round(
                              (reviewer.includeCount / reviewer.totalScreened) * 100,
                            )
                          : 0}
                        %)
                      </span>
                    </td>
                    <td className="p-2 text-center text-red-600 dark:text-red-400">
                      {reviewer.excludeCount}
                      <span className="text-muted-foreground ml-1 text-xs">
                        (
                        {reviewer.totalScreened > 0
                          ? Math.round(
                              (reviewer.excludeCount / reviewer.totalScreened) * 100,
                            )
                          : 0}
                        %)
                      </span>
                    </td>
                    <td className="p-2 text-center text-yellow-600 dark:text-yellow-400">
                      {reviewer.maybeCount}
                      <span className="text-muted-foreground ml-1 text-xs">
                        (
                        {reviewer.totalScreened > 0
                          ? Math.round(
                              (reviewer.maybeCount / reviewer.totalScreened) * 100,
                            )
                          : 0}
                        %)
                      </span>
                    </td>
                    <td className="text-muted-foreground p-2 text-center">
                      {reviewer.averageTimeSeconds != null
                        ? `${Math.round(reviewer.averageTimeSeconds)}s`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-6">
        <h3 className="mb-4 text-lg font-semibold">Status Breakdown</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-muted/30 rounded-lg p-3">
            <div className="text-muted-foreground text-sm">Pending</div>
            <div className="text-xl font-medium">{agreementStats.pending}</div>
          </div>
          <div className="bg-muted/30 rounded-lg p-3">
            <div className="text-muted-foreground text-sm">Awaiting 2nd Review</div>
            <div className="text-xl font-medium">{agreementStats.awaitingSecond}</div>
          </div>
          <div className="rounded-lg bg-green-100 p-3 dark:bg-green-900/30">
            <div className="text-sm text-green-700 dark:text-green-300">Agreed</div>
            <div className="text-xl font-medium text-green-700 dark:text-green-300">
              {agreementStats.agreed}
            </div>
          </div>
          <div className="rounded-lg bg-orange-100 p-3 dark:bg-orange-900/30">
            <div className="text-sm text-orange-700 dark:text-orange-300">Conflicts</div>
            <div className="text-xl font-medium text-orange-700 dark:text-orange-300">
              {agreementStats.conflict}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
